<template>
  <main class="w-full">
    <section class="w-full py-[50px] md:py-24 max-sm:px-6 max-xl:px-13 lg:bg-grey-dark-900">
      <div class="max-w-content mx-auto flex flex-col lg:flex-row items-center text-center lg:text-left gap-14">
        <div class="flex flex-col gap-4 md:gap-10 max-w-3xl lg:w-5/12">
          <p class="text-md font-semibold w-full">BOOST</p>
          <h1 class="text-4xl lg:text-6xl font-semibold text-grey-dark-50 w-full">Save smarter, earn more</h1>
          <p class="text-md md:text-xl font-normal text-grey-dark-300 w-full">LayUp's new savings and rewards platform is here.</p>
          <div class="flex flex-col-reverse sm:flex-row items-center justify-center lg:justify-start gap-4 mt-6">
            <router-link :to="{ name: 'business-enquiry' }" class="w-full sm:w-auto">
              <Button size="2xl" class="w-full sm:w-auto py-6">Talk to sales</Button>
            </router-link>
          </div>
        </div>
        <AnimationComponent class="lg:w-7/12">
          <img :src="isDesktop ? boostProductImage : boostProductMobile" alt="Boost Product Image" class="w-full h-full object-cover" />
        </AnimationComponent>
      </div>
    </section>
    <div class="max-sm:mx-6 max-xl:mx-13">
      <Separator class="bg-grey-dark-800" />
    </div>
    <section class="w-full py-[50px] md:py-24 max-sm:px-6 max-xl:px-13">
      <div class="max-w-content mx-auto">
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center gap-10">
            <HeadingWithIcon
              :icon="getIconComponent('Zap')"
              title="Easy setup with Shopify"
              description="Seamlessly connect your brand through our Shopify plugin."
            />
            <img
              :src="boostConnectionImage"
              alt="Seamlessly connect your brand through our Shopify plugin."
              class="w-auto md:w-150 h-[146px] md:h-60"
            />
          </div>
        </AnimationComponent>
        <div class="my-[50px] md:my-24">
          <Separator class="bg-grey-dark-800" />
        </div>
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center gap-10">
            <div class="w-full max-w-3xl">
              <NumberCounter class="text-boost-500" :counters="revenueCounters" />
            </div>
          </div>
        </AnimationComponent>
      </div>
    </section>
    <div class="max-sm:mx-6 max-xl:mx-13">
      <Separator class="bg-grey-dark-800" />
    </div>
    <FeaturesSection
      :features="features"
      titlePrefix="What is "
      :titleIcon="boostWhite"
      titleSuffix="?"
      description="BOOST is a savings-powered rewards platform that helps convert window shoppers into paying customers, driving down CAC and increasing AOV at checkout."
      :items-per-row="isDesktop ? 4 : 2"
    />
    <div class="max-sm:mx-6 max-xl:mx-13 lg:hidden">
      <Separator class="bg-grey-dark-800" />
    </div>
    <TitleFeaturesSection
      :features="smartInstallmentFeatures"
      title="How BOOST works for you"
      description="BOOST rewards intent, not just clicks."
    />
    <div class="max-sm:mx-6 max-xl:mx-13">
      <Separator class="bg-grey-dark-800" />
    </div>
    <section class="w-full pt-[50px] md:pt-24 max-sm:px-6 max-xl:px-13">
      <div class="max-w-content mx-auto">
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center gap-5 mb-12">
            <h2 class="max-w-3xl text-3xl md:text-4xl font-semibold text-white">Easy integration</h2>
            <p class="max-w-3xl text-md md:text-xl font-light text-grey-dark-300">
              Add LayUp's plugin to your e-commerce store or let us include you in our store directory.
            </p>
          </div>
        </AnimationComponent>
        <AnimationComponent>
          <div class="flex flex-col items-center justify-center text-center gap-10">
            <img
              :src="integrationImage"
              alt="Automate tracking, reconciliation and reporting"
              class="w-full h-full rounded-t-2xl border-6 border-grey-dark-700 border-b-0 object-cover"
            />
          </div>
        </AnimationComponent>
      </div>
    </section>
    <PaymentMethodsSection
      title="Select from multiple payment methods"
      description="Enable the payment options that best suit your customer audience."
      link-to-page="business-pricing-boost"
    />
    <TitleFeaturesSection
      :features="cloudPaymentFeatures"
      title="How it works for your customers"
      description="Getting started is simple:"
    />
    <div class="max-sm:mx-6 max-xl:mx-13">
      <Separator class="bg-grey-dark-800" />
    </div>
    <ApiFeatures
      :use-alternative-tag-text-color="true"
      :items-per-row="isDesktop ? 3 : 2"
      class="lg:px-24"
      :add-padding-x="!isDesktop"
    />
    <div class="max-sm:mx-6 max-xl:mx-13 pb-[42px] lg:pb-0 lg:hidden">
      <Separator class="bg-grey-dark-800" />
    </div>
    <CtaSection
      title="Get started with LayUp"
      description="Low transaction fees and flexible settlements."
      color="brand-900"
      :reverse-buttons="true"
      :learn-more-variant="isDesktop ? 'outline' : 'white'"
      :reduce-padding-top-bottom="!isDesktop"
      :add-padding-x="!isDesktop"
    />
    <LatestBlogs :is-dark-background="true" class="md:pt-24" />
  </main>
</template>

<script setup lang="ts">
import integrationImage from '@/assets/images/business/boost-easy-integration.webp';
import boostConnectionImage from '@/assets/images/business/boost-easy-setup.webp';
import boostProductImage from '@/assets/images/business/boost-product-image.webp';
import boostProductMobile from '@/assets/images/business/boost-product-mobile.png';
import AnimationComponent from '@/components/AnimationComponent.vue';
import CtaSection from '@/components/business/CtaSection.vue';
import FeaturesSection from '@/components/business/FeaturesSection.vue';
import PaymentMethodsSection from '@/components/business/PaymentMethodsSection.vue';
import TitleFeaturesSection from '@/components/business/TitleFeaturesSection.vue';
import { Button } from '@/components/ui/button';
import Separator from '@/components/ui/separator/SeparatorComponent.vue';
import ApiFeatures from '../../components/business/FeatureApi.vue';
import HeadingWithIcon from '../../components/business/HeadingWithIcon.vue';
import NumberCounter from '../../components/NumberCounter.vue';
import { getIconComponent } from '../../utils/icons';
import boostWhite from '@/assets/images/boost-logo-white.svg';
import UsersUp from '@/assets/images/users-up.svg'
import PuzzlePiece from '@/assets/images/puzzle-piece.svg'
import UserEdit from '@/assets/images/user-edit.svg'
import Target from '@/assets/images/target.svg'
import PiggyBank from '@/assets/images/piggy-bank.svg'
import Award from '@/assets/images/award.svg'
import Gift from '@/assets/images/gift.svg'
import { useMediaQuery } from '@vueuse/core';
import LatestBlogs from '@/common/LatestBlogs.vue';

const isDesktop = useMediaQuery('(min-width: 1024px)');

const revenueCounters = [
  {
    number: 79,
    unit: '%',
    description: 'of customers drop off before they get to checkout.',
  },
  {
    number: 36,
    unit: '%',
    description: 'More brands see increased revenue via rewards and loyalty programs.',
  },
  {
    number: 90,
    unit: '%',
    description: 'of loyalty programme owners reported positive ROI.',
  },
];

const features = [
  {
    icon: 'Percent',
    title: 'Grow sales',
    description: 'Keep customers engaged at checkout with a save-now incentive.',
  },
  {
    icon: 'TrendingUp',
    title: 'Increase conversions',
    description: 'BOOST brings high-intent buyers back to you',
  },
  {
    icon: 'CreditCard',
    title: 'Improve cash flow',
    description: 'Lock in future sales with upfront deposits.',
  },
  {
    image: UsersUp,
    title: 'Enhance loyalty',
    description: 'Keep customers engaged and committed to their purchase.',
  },
];

const smartInstallmentFeatures = [
  {
    icon: 'Globe',
    title: '1. Install the Shopify plugin',
    description:
      'Add the BOOST plugin to your Shopify store and place the payment widget anywhere in your sales funnel.',
  },
  {
    image: PuzzlePiece,
    useImageIcon: true,
    title: '2. Set savings threshold',
    description: 'Set the savings threshold you want your customers to reach to get rewarded.',
  },
  {
    icon: 'Settings',
    title: '3. Set reward threshold',
    description: 'Set the reward threshold customers unlock once they reach their savings goal.',
  },
  {
    icon: 'TrendingUp',
    title: '4. Reward unlocks at completion',
    description: 'When the customer reaches their goal, BOOST automatically issues the reward, and you get settled.',
  },
];

const cloudPaymentFeatures = [
  {
    image: UserEdit,
    useImageIcon: true,
    title: 'Sign up for free',
    description: 'Simply create an account and opt in for LayUp BOOST.',
  },
  {
    image: Target,
    useImageIcon: true,
    title: 'Set goal amount',
    description: 'Activate the savings plan by setting the goal and down payment amount.',
  },
  {
    image: PiggyBank,
    useImageIcon: true,
    title: 'Start saving',
    description: 'Customers save over time and get notified throughout the journey.',
  },
  {
    image: Award,
    useImageIcon: true,
    title: 'Receive a BOOST Reward',
    description: 'Upon reaching your goal, you will receive a BOOST Reward added to your savings amount.',
  },
  {
    image: Gift,
    useImageIcon: true,
    title: 'Redeem voucher',
    description:
      'Customers receive a voucher code to complete their purchases on your website at checkout and buy big.',
  },
];
</script>
